import type { Collection, InstagramMetric, InstagramReel, ReelDetail } from '../api/instagramAnalytics'

export const ANALYTICS_PERIODS = [1, 3, 7, 14, 30, 60, 90] as const
export const periodLabel = (days: number) =>
  days === 1 ? '1 день' : days === 3 ? '3 дня' : `${days} дней`
export type AnalyticsSort =
  | 'latest'
  | 'views'
  | 'follows'
  | 'sends'
  | 'reposts'
  | 'comments'
  | 'saves'
  | 'reach'
  | 'likes'
  | 'profile_visits'
  | 'rate_follows'
  | 'rate_sends'
  | 'rate_reposts'
  | 'rate_comments'
  | 'rate_saves'
  | 'rate_profile_visits'
export const SORT_GROUPS: { label: string; options: { value: AnalyticsSort; label: string }[] }[] =
  [
    {
      label: 'Количество',
      options: [
        { value: 'views', label: 'Больше просмотров' },
        { value: 'follows', label: 'Больше подписок' },
        { value: 'profile_visits', label: 'Больше посещений профиля' },
        { value: 'sends', label: 'Больше действий «Поделились»' },
        { value: 'reposts', label: 'Больше репостов' },
        { value: 'comments', label: 'Больше комментариев' },
        { value: 'saves', label: 'Больше сохранений' },
        { value: 'reach', label: 'Больше зрителей' },
        { value: 'likes', label: 'Больше отметок «Нравится»' },
      ],
    },
    {
      label: 'На 1 000 просмотров',
      options: [
        { value: 'rate_follows', label: 'Подписок на 1 000 просмотров' },
        { value: 'rate_profile_visits', label: 'Посещений на 1 000 просмотров' },
        { value: 'rate_sends', label: 'Поделились на 1 000 просмотров' },
        { value: 'rate_reposts', label: 'Репостов на 1 000 просмотров' },
        { value: 'rate_comments', label: 'Комментариев на 1 000 просмотров' },
        { value: 'rate_saves', label: 'Сохранений на 1 000 просмотров' },
      ],
    },
  ]

/** Прежний выбор зрителей теперь означает официальный охват, без private fallback. */
export function normalizeAnalyticsSort(
  value: unknown,
  fallback: AnalyticsSort = 'latest',
): AnalyticsSort {
  if (value === 'viewers' || value === 'viewer_count') return 'reach'
  return value === 'latest' ||
    SORT_GROUPS.some((group) => group.options.some((option) => option.value === value))
    ? value as AnalyticsSort
    : fallback
}

export type ReelDetailTab = 'overview' | 'engagement'
export const REEL_DETAIL_TABS: readonly (readonly [ReelDetailTab, string])[] = [
  ['overview', 'Обзор'],
  ['engagement', 'Вовлечённость'],
]
export function normalizeReelDetailTab(value: string | null): ReelDetailTab {
  return value === 'engagement' ? value : 'overview'
}

export const REEL_REACH_HINT =
  'Уникальные аккаунты, охваченные этим роликом, по оценке Instagram. Официальная метрика reach из Instagram Graph.'
export const COUNT_LABELS: Record<string, string> = {
  views: 'Просмотры',
  reach: 'Охват',
  follows: 'Подписки',
  profile_visits: 'Посещения профиля',
  bio_link_clicks: 'Клики по ссылке в БИО',
  likes: 'Отметки «Нравится»',
  comments: 'Комментарии',
  sends: 'Поделились',
  reposts: 'Репосты',
  saves: 'Сохранения',
  followers: 'Подписчики',
  followers_count: 'Подписчики',
  net_follows: 'Чистый прирост',
  unfollows: 'Отмены подписки',
  total_interactions: 'Взаимодействия',
  interactions: 'Взаимодействия',
  avg_watch_seconds: 'Среднее время просмотра',
  total_watch_seconds: 'Время просмотра',
  skip_rate: 'Доля пропусков',
}
export const RATE_LABELS: Record<string, string> = {
  follows: 'Подписок',
  profile_visits: 'Посещений',
  sends: 'Поделились',
  reposts: 'Репостов',
  comments: 'Комментариев',
  saves: 'Сохранений',
}
export function metricValue(metric: InstagramMetric | undefined): number | null {
  return metric?.status === 'available' && metric.value !== null && Number.isFinite(metric.value)
    ? metric.value
    : null
}
export function metricText(metric: InstagramMetric | undefined, rate = false): string {
  const value = metricValue(metric)
  return value === null
    ? '—'
    : new Intl.NumberFormat('ru-RU', { maximumFractionDigits: rate ? 2 : 0 }).format(value)
}

/** Крупные счётчики остаются читаемыми в карточке; точное число доступно в title. */
export function compactMetricText(metric: InstagramMetric | undefined): string {
  const value = metricValue(metric)
  if (value === null || Math.abs(value) < 1_000_000) return metricText(metric)
  return new Intl.NumberFormat('ru-RU', { notation: 'compact', maximumFractionDigits: 1 }).format(
    value,
  )
}
export function metricHint(metric: InstagramMetric | undefined): string {
  if (metric?.status === 'invalid') return 'Instagram изменил формат этого среза'
  if (metric?.status === 'insufficient_history')
    return 'Для этого периода ещё нет начального замера'
  if (metric?.status === 'counter_reset')
    return 'Счётчик изменился: нужны новые сопоставимые замеры'
  if (metric?.status === 'unsupported') return 'Этот показатель пока не подключён'
  if (metricValue(metric) === null) return 'Нет данных'
  const notes: string[] = []
  if (metric?.coverage === 'since_publication')
    notes.push('За время с публикации внутри выбранного периода')
  if (metric?.window_since && metric.window_until)
    notes.push(
      `Интервал замеров: ${dateLabel(metric.window_since, true)} — ${dateLabel(metric.window_until, true)}`,
    )
  if (metric?.observed_at) notes.push(`Замер: ${dateLabel(metric.observed_at, true)}`)
  if (metric?.source === 'instagram_screenshot') {
    notes.push('Источник: скриншот, распознанные данные')
    if (metric.uploaded_at) notes.push(`Загружен: ${dateLabel(metric.uploaded_at, true)}`)
    return notes.join('. ')
  }
  if (metric?.source)
    notes.push(
      ['official', 'instagram_official_graph'].includes(metric.source)
        ? 'Источник: Instagram Graph'
        : ['private', 'instagram_private_edits'].includes(metric.source)
          ? 'Источник: Instagram Edits'
          : 'Источник: сохранённый замер',
    )
  return notes.join('. ')
}
export function sortReels(items: readonly InstagramReel[], savedSort: string): InstagramReel[] {
  const sort = normalizeAnalyticsSort(savedSort)
  const result = [...items]
  if (sort === 'latest')
    return result.sort((a, b) => (b.published_at ?? '').localeCompare(a.published_at ?? ''))
  const rate = sort.startsWith('rate_')
  const field = rate ? sort.slice(5) : sort
  return result.sort((a, b) => {
    const av = metricValue((rate ? a.rates : a.metrics)[field])
    const bv = metricValue((rate ? b.rates : b.metrics)[field])
    if (av === null) return bv === null ? 0 : 1
    if (bv === null) return -1
    return bv - av
  })
}

/** Создание определяет порядок списка, редактирование не поднимает старую подборку. */
export function newestCollections(items: readonly Collection[]): Collection[] {
  return [...items].sort(
    (a, b) => b.created_at.localeCompare(a.created_at) || b.id.localeCompare(a.id),
  )
}
export function validDateRange(from: string, to: string): boolean {
  const valid = (s: string) =>
    /^\d{4}-\d{2}-\d{2}$/.test(s) &&
    Number.isFinite(Date.parse(s)) &&
    new Date(s).toISOString().slice(0, 10) === s
  return valid(from) && valid(to) && from <= to
}
export function dateLabel(value: string | null | undefined, time = false): string {
  if (!value || !Number.isFinite(Date.parse(value))) return 'Нет даты'
  return new Date(value).toLocaleString('ru-RU', {
    timeZone: 'Europe/Moscow',
    day: 'numeric',
    month: 'short',
    ...(time ? { hour: '2-digit', minute: '2-digit' } : { year: 'numeric' }),
  })
}

export interface ReelHistorySeries {
  id: 'instagram' | 'graph' | 'edits'
  label: string
  color: string
  dashed?: boolean
  points: { x: number; y: number | null; label: string }[]
}

/** У каждого источника свой ряд: ни замены сохранённой кривой, ни склейки счётчиков. */
export function reelHistorySeries(
  reel: Pick<ReelDetail, 'views_timeline' | 'history' | 'legacy_history'>,
  field: string,
): ReelHistorySeries[] {
  const series: ReelHistorySeries[] = []
  const timeline = reel.views_timeline
  if (
    field === 'views' &&
    timeline?.status === 'available' &&
    timeline.time_axis === 'datetime' &&
    timeline.value_unit === 'views'
  ) {
    const points = timeline.points
      .map((point) => ({
        x: Date.parse(point.at),
        y:
          point.views !== null && Number.isFinite(point.views) && point.views >= 0
            ? point.views
            : null,
        label: dateLabel(point.at, true),
      }))
      .sort((a, b) => a.x - b.x)
    if (
      points.every((point, index) =>
        Number.isFinite(point.x) && (index === 0 || point.x > points[index - 1]!.x),
      ) &&
      points.some((point) => point.y !== null)
    )
      series.push({
        id: 'instagram',
        label: 'Instagram · сохранённая история',
        color: 'var(--stage-violet-fg)',
        points,
      })
  }
  const channels = [
    {
      id: 'graph', label: 'Graph · наши замеры', source: 'instagram_official_graph',
      color: 'var(--link)', rows: reel.history,
    },
    {
      id: 'edits', label: 'Edits · прежние замеры', source: 'instagram_private_edits',
      color: 'var(--muted)', rows: reel.legacy_history ?? [],
    },
  ] as const
  for (const channel of channels) {
    const points = channel.rows
      .filter((row) => row.source === channel.source && Number.isFinite(Date.parse(row.observed_at)))
      .map((row) => ({
        x: Date.parse(row.observed_at),
        y: row.metrics[field]?.source === channel.source ? metricValue(row.metrics[field]) : null,
        label: dateLabel(row.observed_at, true),
      }))
      .sort((a, b) => a.x - b.x)
    if (points.some((point) => point.y !== null))
      series.push({
        id: channel.id, label: channel.label, color: channel.color,
        dashed: channel.id === 'edits', points,
      })
  }
  return series
}

/** Сетка календаря сохраняет пропущенные дни и реальные расстояния между точками. */
export function calendarPoints(
  rows: { date: string; metrics: Record<string, InstagramMetric> }[],
  field: string,
  from: string,
  to: string,
) {
  if (!validDateRange(from, to)) return []
  const byDate = new Map(rows.map((row) => [row.date, row]))
  const points: { x: number; y: number | null; label: string }[] = []
  for (
    let day = Date.parse(`${from}T00:00:00Z`), end = Date.parse(`${to}T00:00:00Z`);
    day <= end;
    day += 86_400_000
  ) {
    const date = new Date(day).toISOString().slice(0, 10)
    points.push({
      x: day,
      y: metricValue(byDate.get(date)?.metrics[field]),
      label: dateLabel(date),
    })
  }
  return points
}

export function analyticsLink(path: string, params: URLSearchParams) {
  const next = new URLSearchParams(params)
  next.delete('detail')
  return `${path}?${next}`
}

export function moscowToday(now = new Date()): string {
  const parts = new Intl.DateTimeFormat('en', {
    timeZone: 'Europe/Moscow',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(now)
  const get = (kind: string) => parts.find((p) => p.type === kind)?.value ?? ''
  return `${get('year')}-${get('month')}-${get('day')}`
}

export function countryLabel(value: string): string {
  try {
    return new Intl.DisplayNames(['ru'], { type: 'region' }).of(value) ?? value
  } catch {
    return value
  }
}

export function percentageText(value: number | null | undefined): string {
  return typeof value === 'number' && Number.isFinite(value)
    ? `${new Intl.NumberFormat('ru-RU', { maximumFractionDigits: 1 }).format(value)}%`
    : '—'
}
